import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { countryFlagUrl } from "@/lib/countries";
import { toast } from "sonner";
import { Check, X, Trash2 } from "lucide-react";

export default function AdminCaptainsPage() {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/captains");
    setItems(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (u, approved) => {
    try {
      await api.post(`/admin/captains/${u.id}/approve`, { approved });
      toast.success(approved ? "Approved" : "Revoked");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (u) => {
    if (!confirm(`Delete captain ${u.email}?`)) return;
    try { await api.delete(`/admin/captains/${u.id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const reject = async (u) => {
    const reason = prompt(
      `Reject captain ${u.email}?\n\nOptional note to include in the rejection email (leave blank to send a standard message):`,
      "",
    );
    // prompt() returns null on cancel; empty string is allowed (= no reason).
    if (reason === null) return;
    try {
      await api.post(`/admin/captains/${u.id}/reject`, { reason: reason || null });
      toast.success("Rejected — captain notified by email");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pending = items.filter((u) => !u.approved);
  const approved = items.filter((u) => u.approved);

  return (
    <div className="p-8 lg:p-10" data-testid="admin-captains">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Captains</div>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2 mb-8">Captain approvals</h1>

      <Section title="Pending approval" empty="No pending captains">
        {pending.map((u) => (
          <Row key={u.id} u={u}>
            <Button size="sm" onClick={() => toggle(u, true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`approve-${u.id}`}>
              <Check className="h-4 w-4 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => reject(u)} data-testid={`reject-${u.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </Row>
        ))}
        {pending.length === 0 && <div className="text-muted-foreground p-4">No pending captains.</div>}
      </Section>

      <Section title="Approved captains" empty="None">
        {approved.map((u) => (
          <Row key={u.id} u={u}>
            <Badge className="bg-primary text-primary-foreground">Approved</Badge>
            <Button size="sm" variant="ghost" onClick={() => toggle(u, false)} data-testid={`revoke-${u.id}`}>
              <X className="h-4 w-4 mr-1" /> Revoke
            </Button>
            <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`delete-${u.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </Row>
        ))}
        {approved.length === 0 && <div className="text-muted-foreground p-4">None yet.</div>}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-display text-xl font-bold mb-4">{title}</h2>
      <div className="border border-border bg-white">{children}</div>
    </section>
  );
}
function Row({ u, children }) {
  return (
    <div className="flex items-center gap-4 p-4 border-b border-border last:border-b-0" data-testid={`captain-row-${u.id}`}>
      <img src={countryFlagUrl(u.country_code)} alt="" className="h-7 w-10 object-cover border border-border" />
      <div className="flex-1">
        <div className="font-medium">{u.name} <span className="text-xs text-muted-foreground">({u.country_name})</span></div>
        <div className="text-xs text-muted-foreground">{u.email}</div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
